import { createContext, Dispatch, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { BookmarkActions, BookmarkActionTypes } from './actions';
import { ListItemData, MapDataUtils } from '../../pages/networkDoctors/utils/mapData';
import { reducer } from './reducer';
import { useCreateBookmark, useDeleteBookmark, useGetDetailedBookmarks } from 'all-in-one-shared-api';
import { useKeycloak } from 'all-in-one-react-keycloak';

export const defaultValue = {
  state: [],
  dispatch: () => null,
};
export const BookmarkContext = createContext<{
  state?: ListItemData[];
  dispatch: Dispatch<BookmarkActions>;
}>(defaultValue);
export const DetailedBookmarkKey = 'getDetailedBookmarks';

export const useBookmarkContext = () => {
  const { state, dispatch } = useContext(BookmarkContext);
  const { keycloak } = useKeycloak();
  const { createBookmark: createBookmarkAPI } = useCreateBookmark(keycloak.token || '');
  const { deleteBookmark: deleteBookmarkAPI } = useDeleteBookmark(keycloak.token || '');

  const addBookmark = async (item: ListItemData) => {
    const { itemId, itemType } = item;
    const newBookmark = itemId && (await createBookmarkAPI(itemType, itemId));
    if (newBookmark) {
      dispatch({
        type: BookmarkActionTypes.ADD,
        payload: { ...item, bookmarkId: newBookmark.id },
      });
    }
  };

  const removeBookmark = async (bookmarkId: number) => {
    const deleteRes = await deleteBookmarkAPI(bookmarkId);
    if (deleteRes.success) {
      dispatch({
        type: BookmarkActionTypes.REMOVE,
        payload: { bookmarkId },
      });
    }
  };

  const filteredBookmark = useCallback(
    (districts: string[] | undefined, specialties: string[], searchQuery: string) => {
      const _districts = (districts || []).map((d) => d.toLowerCase());
      const _specialties = (specialties || []).map((s) => s.toLowerCase());

      return (state || [])
        .filter((d) => {
          return _districts.length ? _districts.includes(d.data?.district?.toLowerCase() ?? '') : true;
        })
        .filter((d) => {
          return _specialties.length ? _specialties.includes(d.data?.specialty?.toLowerCase() ?? '') : true;
        })
        .filter((d) => {
          return searchQuery?.length
            ? d.title2?.toLowerCase().includes(searchQuery) || d.title2_cn?.toLowerCase().includes(searchQuery)
            : true;
        });
    },
    [state],
  );

  return {
    addBookmark,
    removeBookmark,
    bookmark: state,
    filteredBookmark,
  };
};

type BookmarkContextProviderProps = {
  children: ReactNode;
};

export const BookmarkContextProvider = ({ children }: BookmarkContextProviderProps): JSX.Element => {
  const [state, dispatch] = useReducer(reducer, []);
  const { keycloak } = useKeycloak();
  const { shouldFetch, data } = useGetDetailedBookmarks(DetailedBookmarkKey, keycloak.token || '');

  const mappedBookmark = useMemo(() => {
    return (data || []).map(({ clinic, hospital, id }) => {
      const bmClinic = clinic && MapDataUtils.mapData(clinic);
      const bmHospital = hospital && MapDataUtils.mapData(hospital);

      return { ...bmHospital, ...bmClinic, bookmarkId: id } as ListItemData;
    });
  }, [data]);

  useEffect(() => {
    if (keycloak.authenticated) shouldFetch(true);
  }, [shouldFetch, keycloak.authenticated]);

  useEffect(() => {
    if (mappedBookmark) dispatch({ type: BookmarkActionTypes.SET, payload: mappedBookmark });
  }, [mappedBookmark, dispatch]);

  return <BookmarkContext.Provider value={{ state, dispatch }}>{children}</BookmarkContext.Provider>;
};
